import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ContentPageTheme, Form, ReviewWidgetConfig, ReviewWidgetConfigServices } from "@/services/bedrock";
import { ConfigSkeleton } from "../components/LoadingStates";
import { PreviewPlayerState } from "../components/usePreviewPlayer";
import { ReviewWidgetConfigForm } from "./components/ReviewWidgetConfigForm";
import { ReviewWidgetPreview } from "./components/ReviewWidgetPreview";
import { ReviewWidgetPreviewControls } from "./components/ReviewWidgetPreviewControls";
import { useReviewWidgetPreview } from "./components/useReviewWidgetPreview";

interface ViewProps {
  themes: ContentPageTheme[];
  forms: Form[];
  preview: PreviewPlayerState;
}

interface ReviewWidgetContentProps extends ViewProps {
  config: ReviewWidgetConfig;
  updateConfig: (updates: Partial<ReviewWidgetConfig>) => void;
}

function ReviewWidgetContent({ config, updateConfig, themes, forms, preview }: ReviewWidgetContentProps) {
  const { controlsProps, previewProps } = useReviewWidgetPreview({
    config,
    themes,
    forms,
    previewPlayer: preview.previewPlayer,
  });

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)] lg:min-h-[calc(100vh-14rem)] lg:min-w-0">
      <div className="@container p-1 flex flex-col gap-6 min-w-0 lg:min-h-0 lg:overflow-y-auto no-scrollbar">
        <ReviewWidgetConfigForm config={config} updateConfig={updateConfig} themes={themes} forms={forms} />
        <ReviewWidgetPreviewControls preview={preview} {...controlsProps} />
      </div>

      <div className="p-1 min-w-0 lg:min-h-0 lg:overflow-y-auto no-scrollbar">
        <ReviewWidgetPreview previewDevice={preview.previewDevice} {...previewProps} />
      </div>
    </div>
  );
}

export function ReviewWidget({ themes, forms, preview }: ViewProps) {
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState<ReviewWidgetConfig | null>(null);

  useEffect(() => {
    setLoading(true);
    ReviewWidgetConfigServices.getConfigs()
      .then(async configs => {
        if (configs.length > 0) {
          setConfig(configs[0]);
        } else {
          const created = await ReviewWidgetConfigServices.createConfig({
            approvedForSocialOnly: true,
            reviewSortType: "featured",
            autoScrollEnabled: true,
          });
          setConfig(created);
        }
      })
      .catch(error => {
        console.error(error);
        toast.error("Error loading review widget settings");
      })
      .finally(() => setLoading(false));
  }, []);

  const updateConfig = (updates: Partial<ReviewWidgetConfig>) => {
    setConfig(current => (current ? { ...current, ...updates } : current));
  };

  return (
    <div className="flex flex-col gap-4">
      {loading || !config ? (
        <ConfigSkeleton />
      ) : (
        <ReviewWidgetContent
          config={config}
          updateConfig={updateConfig}
          themes={themes}
          forms={forms}
          preview={preview}
        />
      )}
    </div>
  );
}
